import { useSuspenseQuery } from '@tanstack/react-query'
import { AuthContext } from '@urbanx/agx.auth.client'
import { useCallback, useContext } from 'react'
import RexService from 'services/rexService'
import { PartnershipDetailsPartnershipLevel } from '@urbanx/agx.agencies.client'

export interface CurrentReviewPeriodPartnershipInformation {
  partnershipLevel: PartnershipDetailsPartnershipLevel
  nextReviewDate: Date
  currentCommission: number
  commissionTargetAdjustment: number
}

type StaffGetCurrentPartnershipInformationKey = [
  'StaffGetCurrentPartnershipInformation',
  string, // agencyId
  string | undefined, // anniversaryDate
  boolean | undefined, // isPreviousYear
]

const useStaffGetCurrentPartnershipInformation = (
  agencyId: string,
  anniversaryDate: string | undefined = undefined,
  isPreviousYear: boolean | undefined = undefined
): {
  data: CurrentReviewPeriodPartnershipInformation | undefined
  isPending: boolean
  isError: boolean
} => {
  const { getAuthToken } = useContext(AuthContext)

  const staffGetCurrentPartnershipInformation = useCallback(
    async ([
      url,
      agencyId,
      anniversaryDate,
      isPreviousYear,
    ]: StaffGetCurrentPartnershipInformationKey) => {
      try {
        const token = await getAuthToken()

        const service = new RexService(token)

        const params: {
          agencyId: string
          specificAnniversaryDate?: string
          previousYear?: boolean
        } = {
          agencyId,
        }

        if (anniversaryDate) {
          params.specificAnniversaryDate = anniversaryDate
        }

        if (isPreviousYear) {
          params.previousYear = isPreviousYear
        }

        const result = await service.rexService.get(url, params)

        return result.data
      } catch (error) {
        console.error('Error fetching agency partnership details:', error)
        throw new Error(
          `Failed to fetch agency partnership details for agencyId: ${agencyId}, anniversaryDate: ${anniversaryDate}`
        )
      }
    },
    [getAuthToken]
  )

  const { data, isPending, isError } = useSuspenseQuery<
    CurrentReviewPeriodPartnershipInformation,
    Error,
    CurrentReviewPeriodPartnershipInformation,
    StaffGetCurrentPartnershipInformationKey
  >({
    queryKey: [
      'StaffGetCurrentPartnershipInformation',
      agencyId,
      anniversaryDate,
      isPreviousYear,
    ],
    queryFn: (queryInfo) =>
      staffGetCurrentPartnershipInformation(queryInfo.queryKey),
  })

  return { data, isPending, isError }
}

export default useStaffGetCurrentPartnershipInformation
